import { create } from "zustand"
import { combine } from "zustand/middleware"
import {
  encodeOpenDrawer,
  printFullReceipt,
  printIminCartTransactionReceiptItem,
  printIminCartTransactionReceiptItemHeader,
  printIminCartTransactionReceiptMetadata,
  printIminCartTransactionReceiptTotal,
  createRefundSummaryBuffer,
  printIminRefundSummary,
  RefundReceiptDetails,
} from "../lib/thermal-printer"
import {
  CartItem,
  DEFAULT_MPOS_SETTING,
  KitchenPrinterOptions,
} from "../lib/models"
import { GetCartTransactionResponse } from "./api/use-cart-transaction"
import { InvoiceSettingsResponseBody } from "./api/use-invoice-settings"
import { generateVariantNameFromVariantValues } from "../lib/variants"
import IminPrinter from "../lib/imin-printer"
import {
  encodeFullKitchenPrinter,
  encodeKitchenPrinter,
  KitchenPrinterItem,
} from "../lib/thermal-printer/kitchen-printer"
import { encodeBIRReceipt } from "../lib/thermal-printer/bir-receipts"
import { Business } from "./api/use-business"
import { tableQRCCodes } from "../lib/thermal-printer/table-management"
import { cloneDeep } from "lodash"
import { nanoid } from "nanoid"
import { ThermalPrinterEncoderOption } from "../lib/thermal-printer-encoder"
import BluetoothThermalPrinter from "../lib/thermal-printer/bluetooth-thermal-printer"
import UsbThermalPrinter from "../lib/thermal-printer/usb-thermal-printer"
import ThermalPrinter from "../lib/thermal-printer/model"
import * as Sentry from "@sentry/react"
import { sleep } from "../lib/async"
import { GetKitchenPrinterGroupResponse } from "./api/use-kitchen-printer-groups"
import SerialThermalPrinter from "../lib/thermal-printer/serial-thermal-printer"
import { encodeBIRXReport } from "../lib/thermal-printer/bir-x-report"
import { encodeTransferOrder } from "../lib/thermal-printer/transfer-order"
import { StockTransfer } from "./api/use-stock-transfers"
import { encodeShiftReport } from "../lib/thermal-printer/shift-report"
import { encodeItemLabels } from "../lib/thermal-printer/item-labels"
import CompanionAppThermalPrinter from "../lib/thermal-printer/companion-app-thermal-printer"
import { encodeGiftCard } from "../lib/thermal-printer/gift-card"
import { Locations } from "./api/use-locations"
import { GiftCard } from "./api/use-gift-cards"
import {
  DeliveryOrderDetails,
  encodeDeliveryOrder,
} from "../lib/thermal-printer/delivery-order"
import {
  encodePayInOutReceipt,
  type PayInOutReceiptPayload,
} from "../lib/thermal-printer/payInOutReceipt"

// UUID from WebBluetoothCG print demo
// https://github.com/WebBluetoothCG/demos/blob/7b7397df596888b51043586a187a5b2e08f14ecd/bluetooth-printer/index.html#L246
const THERMAL_SERVICE_UUID = "000018f0-0000-1000-8000-00805f9b34fb"
const PRINT_CHARACTERISTIC_UUID = "00002af1-0000-1000-8000-00805f9b34fb"
const UART_SERVICE_UUID = "49535343-fe7d-4ae5-8fa9-9fafd205e455"
const UART_RX_CHARACTERISTIC_UUID = "49535343-8841-43f4-a8d4-ecbe34729bb3"

export enum IminPrintWidth {
  WIDTH_58 = 1,
  WIDTH_80 = 0,
}

const DEFAULT_STATE = {
  receipt: {
    printer: null as ThermalPrinter | null,
    connected: false,
    type: "BLUETOOTH" as "BLUETOOTH" | "USB",
    deviceId: null as string | null,
  },
  kitchenPrinters: [] as Array<{
    printer: ThermalPrinter | null
    connected: boolean
    printerGroup: string
    deviceId: string | null // this is UUID returned by BluetoothDevice.id, so we can persist it
    id: string // this is a non-nullable id for the printer data
    type: "BLUETOOTH" | "USB" | "SERIAL"
  }>,
  label: {
    printer: null as ThermalPrinter | null,
    connected: false,
    deviceId: null as string | null,
    type: "BLUETOOTH" as "BLUETOOTH" | "USB",
    height: undefined as number | undefined,
    width: undefined as number | undefined,
  },
  imin: {
    printer: null as IminPrinter | null,
    connected: false,
    width: IminPrintWidth.WIDTH_80,
  },
}

export const connectPrinter = async (defaultPrinter?: BluetoothDevice) => {
  let printer = defaultPrinter
  if (!printer) {
    printer = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [THERMAL_SERVICE_UUID, UART_SERVICE_UUID],
    })
  }
  Sentry.addBreadcrumb({
    category: "thermal-printer",
    message: "Printer selected",
    data: printer,
  })
  if (!printer.gatt) {
    throw new Error("GATT_SERVER_NOT_FOUND")
  }

  const server = await printer.gatt?.connect()
  const services = await server.getPrimaryServices()
  const thermalService = services.find(
    (service) => service.uuid === THERMAL_SERVICE_UUID
  )
  if (thermalService) {
    const thermalCharacteristic = await thermalService.getCharacteristic(
      PRINT_CHARACTERISTIC_UUID
    )
    return {
      printer,
      server,
      service: thermalService,
      characteristic: thermalCharacteristic,
    }
  }

  const uartService = services.find(
    (service) => service.uuid === UART_SERVICE_UUID
  )
  if (!uartService) {
    Sentry.addBreadcrumb({
      category: "thermal-printer",
      message: "Service not found",
      data: services,
    })
    throw new Error("SERVICE_NOT_FOUND")
  }
  const uartCharacteristic = await uartService.getCharacteristic(
    UART_RX_CHARACTERISTIC_UUID
  )
  return {
    printer,
    server,
    service: uartService,
    characteristic: uartCharacteristic,
  }
}

const useThermalPrinter = create(
  combine(DEFAULT_STATE, (set, get) => ({
    connectReceipt: async (type: "BLUETOOTH" | "USB" = "BLUETOOTH") => {
      let printer: ThermalPrinter
      if (type === "BLUETOOTH") {
        printer = new BluetoothThermalPrinter()
      } else {
        printer = new UsbThermalPrinter()
      }
      await printer.connect()

      printer.onDisconnect(() => {
        const { receipt } = get()
        set({ receipt: { ...receipt, connected: false } })
      })

      set({
        receipt: {
          printer,
          connected: true,
          type,
          deviceId: printer.getId(),
        },
      })
      // Save settings after successful connection
      savePrinterSettings()
    },
    connectKitchenPrinter: async (
      id: string,
      type: "BLUETOOTH" | "USB" | "SERIAL" = "BLUETOOTH"
    ) => {
      const kitchenPrinter = get().kitchenPrinters.find((kp) => kp.id === id)
      if (!kitchenPrinter) {
        throw new Error("PRINTER_NOT_FOUND")
      }

      const { printerGroup } = kitchenPrinter

      let printer = await createThermalPrinter(type)

      printer.onDisconnect(() => {
        const { kitchenPrinters } = get()
        const updated = cloneDeep(kitchenPrinters)
        const kp = updated.find((kp) => kp.id === id)
        if (!kp) {
          return
        }
        kp.connected = false
        set({ kitchenPrinters: updated })
      })

      const updated = cloneDeep(get().kitchenPrinters)
      const kp = updated.find((kp) => kp.id === id)
      if (!kp) {
        return
      }
      kp.printer = printer
      kp.connected = true
      kp.deviceId = printer.getId()
      kp.printerGroup = printerGroup
      kp.type = type
      set({ kitchenPrinters: updated })
      savePrinterSettings()
    },
    setKitchenPrinterGroupName: (id: string, printerGroup: string) => {
      const { kitchenPrinters } = get()
      const updated = cloneDeep(kitchenPrinters)
      const kp = updated.find((kp) => kp.id === id)
      if (!kp) {
        return
      }
      kp.printerGroup = printerGroup
      set({ kitchenPrinters: updated })
      savePrinterSettings()
    },
    connectImin: async () => {
      const state = get()
      if (state.imin.printer) {
        try {
          const status = await state.imin.printer.getPrinterStatus()
          if (status.value === 0) {
            return {
              text: "Printer is already connected",
              value: 0,
            }
          }
        } catch (e) {}
      }
      set({
        imin: {
          ...DEFAULT_STATE.imin,
          width: state.imin.width,
        },
      })

      const printer = new IminPrinter()
      await printer.connect()

      let status
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          printer.initPrinter()
          await sleep(1000) // printer status might not always be updated right away
          status = await printer.getPrinterStatus()
          Sentry.addBreadcrumb({
            category: "thermal-printer",
            message: "Imin printer status",
            data: status,
          })

          if (verifyStatus(status.value)) {
            set({
              imin: { printer, connected: true, width: state.imin.width },
            })
            break
          }
        } catch (e) {
          if (attempt === 2) {
            throw e
          }
        }
      }

      return {
        text: status.text as string,
        value: status.value as number,
      }
    },

    reconnectAll: async () => {
      // Load saved settings
      const savedSettings = loadPrinterSettings()
      if (!savedSettings) return

      // Update state with saved settings
      const { receipt, label, kitchenPrinters } = savedSettings
      const currentState = get()

      // Restore saved settings for imin width
      if (savedSettings.imin?.width) {
        set({ imin: { ...currentState.imin, width: savedSettings.imin.width } })
      }

      // Reconnect receipt printer
      if (receipt?.deviceId) {
        let printer: ThermalPrinter
        if (receipt.type === "BLUETOOTH") {
          printer = new BluetoothThermalPrinter()
        } else {
          printer = new UsbThermalPrinter()
        }
        try {
          await printer.reconnect(receipt.deviceId)

          printer.onDisconnect(() => {
            const { receipt } = get()
            set({ receipt: { ...receipt, connected: false } })
          })

          set({
            receipt: {
              printer,
              connected: true,
              type: receipt.type,
              deviceId: receipt.deviceId,
            },
          })
        } catch (e: any) {
          if (e?.message !== "ALREADY_WATCHING_ADVERTISEMENTS") {
            console.error("Failed to reconnect receipt printer:", e)
          }
        }
      }

      // Reconnect label printer
      if (label?.deviceId) {
        let printer: ThermalPrinter
        if (label.type === "BLUETOOTH") {
          printer = new BluetoothThermalPrinter()
        } else {
          printer = new UsbThermalPrinter()
        }
        try {
          await printer.reconnect(label.deviceId)

          printer.onDisconnect(() => {
            const { label } = get()
            set({ label: { ...label, connected: false } })
          })

          set({
            label: {
              printer,
              connected: true,
              deviceId: label.deviceId,
              type: label.type,
              height: label.height,
              width: label.width,
            },
          })
        } catch (e: any) {
          if (e?.message !== "ALREADY_WATCHING_ADVERTISEMENTS") {
            console.error("Failed to reconnect label printer:", e)
          }
        }
      }

      // Restore kitchen printers configuration first
      if (kitchenPrinters && kitchenPrinters.length > 0) {
        set({
          kitchenPrinters: kitchenPrinters.map((saved: any) => ({
            id: saved.id,
            deviceId: saved.deviceId,
            printerGroup: saved.printerGroup,
            type: saved.type,
            printer: null,
            connected: false,
          })),
        })

        // Reconnect each kitchen printer
        for (const savedKp of kitchenPrinters) {
          if (savedKp.deviceId) {
            let printer: ThermalPrinter
            if (savedKp.type === "BLUETOOTH") {
              printer = new BluetoothThermalPrinter()
            } else if (savedKp.type === "SERIAL") {
              printer = new SerialThermalPrinter()
            } else {
              printer = new UsbThermalPrinter()
            }

            try {
              await printer.reconnect(savedKp.deviceId)

              printer.onDisconnect(() => {
                const { kitchenPrinters } = get()
                const updated = cloneDeep(kitchenPrinters)
                const kp = updated.find((kp) => kp.id === savedKp.id)
                if (kp) {
                  kp.connected = false
                  set({ kitchenPrinters: updated })
                }
              })

              const updated = cloneDeep(get().kitchenPrinters)
              const kp = updated.find((kp) => kp.id === savedKp.id)
              if (kp) {
                kp.printer = printer
                kp.connected = true
                set({ kitchenPrinters: updated })
              }
            } catch (e: any) {
              console.error(
                `Failed to reconnect kitchen printer ${savedKp.id}:`,
                e
              )
            }
          }
        }
      }
    },
    setIminPrintWidth: (width: IminPrintWidth) => {
      set({ imin: { ...get().imin, width } })
      savePrinterSettings()
    },
    connectLabel: async (type: "BLUETOOTH" | "USB" = "BLUETOOTH") => {
      let printer: ThermalPrinter
      if (type === "BLUETOOTH") {
        printer = new BluetoothThermalPrinter()
      } else {
        printer = new UsbThermalPrinter()
      }
      await printer.connect()

      printer.onDisconnect(() => {
        const { label } = get()
        set({ label: { ...label, connected: false } })
      })

      const { label } = get()
      set({
        label: {
          printer,
          connected: true,
          deviceId: printer.getId(),
          type: type,
          height: label.height,
          width: label.width,
        },
      })
      // Save settings after successful connection
      savePrinterSettings()
    },
    write: async (buffer: Uint8Array) => {
      const printer = get().receipt.printer

      if (!printer) {
        throw new Error("NOT_CONNECTED")
      }

      await printer.print(buffer)
    },
    printReceipt: async (
      business: Business,
      trx: GetCartTransactionResponse,
      receiptSetting: InvoiceSettingsResponseBody[number],
      posSetting: typeof DEFAULT_MPOS_SETTING,
      currency: string = "USD",
      options?: {
        triggerOpenDrawer?: boolean
        birReceiptType?: "ORIGINAL" | "DEFAULT"
        birORNumber?: string
        birSeriesNumber?: string
        locationHasTaxes?: boolean
        fullCashRegister?: {
          id: string
          name: string
          min: string
          ptu: string
          ptuDateIssued: string
          serialNo: string
        }
        locationVatType?: string
        refundDetails?: RefundReceiptDetails
      }
    ) => {
      const state = get()

      const triggerOpenDrawer = options?.triggerOpenDrawer ?? true
      const birReceipt = options?.birReceiptType ?? "DEFAULT"

      // imin inbuilt printer
      if (
        !state.receipt.connected &&
        state.imin.connected &&
        state.imin.printer
      ) {
        try {
          const status = await state.imin.printer.getPrinterStatus()
          if (status.value === -1) {
            // SDK is connected but might not be turned on
            state.imin.printer.reconnect()
            await sleep(1000)
            state.imin.printer.initPrinter()
            await sleep(1000)
          }
        } catch (e) {
          console.log("error", e)
          // usually triggered when getPrinterStatus crashes because no socket connection
          // has been setup yet
          await state.imin.printer.connect()
          state.imin.printer.initPrinter()
          await sleep(1000)
        }

        const newStatus = await state.imin.printer.getPrinterStatus()
        verifyStatus(newStatus.value)

        state.imin.printer.setPageFormat(state.imin.width)
        await printIminCartTransactionReceiptMetadata(
          state.imin.printer,
          "Header",
          business,
          trx,
          receiptSetting,
          posSetting
        )
        printIminCartTransactionReceiptItemHeader(state.imin.printer, trx)
        for (const item of trx.orderedProducts) {
          printIminCartTransactionReceiptItem(
            state.imin.printer,
            item,
            currency
          )
        }
        printIminCartTransactionReceiptTotal(
          state.imin.printer,
          trx,
          currency,
          business
        )
        if (options?.refundDetails) {
          printIminRefundSummary(state.imin.printer, options.refundDetails)
        }
        await printIminCartTransactionReceiptMetadata(
          state.imin.printer,
          "Footer",
          business,
          trx,
          receiptSetting,
          posSetting
        )
        state.imin.printer.printAndLineFeed()
        state.imin.printer.printAndLineFeed()
        state.imin.printer.printAndLineFeed()
        state.imin.printer.partialCutPaper()
        if (triggerOpenDrawer) {
          state.imin.printer.openCashBox()
        }

        return
      }

      const encoderOptions: ThermalPrinterEncoderOption = {
        columns: 32,
        language: "esc-pos",
        feedBeforeCut: 1,
      }
      if (receiptSetting.width === 57) {
        encoderOptions.columns = 32
      } else if (receiptSetting.width === 76) {
        encoderOptions.columns = 42
      } else if (receiptSetting.width === 80) {
        encoderOptions.columns = 48
      }

      if (trx.location.birEnabled) {
        const toPrint = await encodeBIRReceipt(
          {
            trx,
            business,
            nextSeriesNumber: options?.birSeriesNumber,
            nextORNumber: options?.birORNumber,
            invoiceSettings: receiptSetting,
            type: birReceipt,
            locationHasTaxes: options?.locationHasTaxes,
            fullCashRegister: options?.fullCashRegister,
            locationVatType: options?.locationVatType,
          },
          encoderOptions
        )
        await state.receipt.printer?.print(toPrint)
        if (options?.refundDetails) {
          const summaryBuffer = createRefundSummaryBuffer(
            options.refundDetails,
            encoderOptions
          )
          await state.receipt.printer?.print(summaryBuffer)
        }
      } else {
        const header = await printFullReceipt(
          business,
          trx,
          receiptSetting,
          posSetting,
          currency,
          encoderOptions,
          options?.locationHasTaxes,
          options?.refundDetails
        )
        await state.receipt.printer?.print(header)
      }

      if (triggerOpenDrawer) {
        const openDrawer = encodeOpenDrawer()
        await state.receipt.printer?.print(openDrawer)
      }
    },
    printLabels: async (
      items: CartItem[],
      customerName?: string,
      orderNumber?: string | number,
      queueNumber?: string | number,
      posSettings?: typeof DEFAULT_MPOS_SETTING,
      locationId?: string
    ) => {
      const { label } = get()

      const printerDpi = 203
      const labelHeightInMm = label.height || 20
      const labelWidthInMm = label.width || 50
      const labelGapInMm = 2

      const dotsPerMm = printerDpi / 25.4
      const labelDotsWidth = Math.max(Math.floor(labelWidthInMm * dotsPerMm), 0)
      const labelDotsHeight = Math.max(
        Math.floor(labelHeightInMm * dotsPerMm),
        0
      )
      const marginX = Math.floor(2 * dotsPerMm)
      const marginY = Math.floor(2 * dotsPerMm)
      let columnGap =
        labelDotsWidth > 0
          ? Math.min(Math.floor(1 * dotsPerMm), labelDotsWidth)
          : 0
      const availableWidth = Math.max(labelDotsWidth - marginX * 2, 0)
      let rightColumnWidth =
        availableWidth > 0
          ? Math.min(Math.floor(availableWidth * 0.35), 150)
          : 0
      if (availableWidth - columnGap - rightColumnWidth <= 0) {
        columnGap = 0
        rightColumnWidth = 0
      }
      const leftColumnWidth = Math.max(
        availableWidth - columnGap - rightColumnWidth,
        0
      )
      const leftColumnX = marginX
      const rightColumnX = marginX + leftColumnWidth + columnGap
      const lineHeight = Math.max(Math.floor(24 * 1.3), 24)
      const charWidth = 8
      const maxCharsLeftColumn = Math.max(
        Math.floor(leftColumnWidth / charWidth),
        1
      )
      const maxCharsRightColumn = Math.max(
        Math.floor((rightColumnWidth || charWidth) / charWidth),
        1
      )

      const sanitizeText = (value: string) =>
        value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')

      const splitLongWord = (word: string, limit: number) => {
        const chunks: string[] = []
        if (!word.length) {
          return chunks
        }
        const step = Math.max(limit, 1)
        for (let index = 0; index < word.length; index += step) {
          chunks.push(word.slice(index, index + step))
        }
        return chunks
      }

      const wrapText = (value: string, limit: number) => {
        const maxChars = Math.max(limit, 1)
        const wrapped: string[] = []
        if (!value?.trim()) {
          return wrapped
        }

        const paragraphs = value.split(/\r?\n/)
        for (const paragraph of paragraphs) {
          const words = paragraph.split(/\s+/).filter(Boolean)
          if (!words.length) {
            continue
          }

          let currentLine = ""
          for (const word of words) {
            const segments =
              word.length > maxChars ? splitLongWord(word, maxChars) : [word]

            for (const segment of segments) {
              if (!currentLine.length) {
                currentLine = segment
                continue
              }

              if (currentLine.length + 1 + segment.length <= maxChars) {
                currentLine = `${currentLine} ${segment}`
              } else {
                wrapped.push(currentLine)
                currentLine = segment
              }
            }
          }

          if (currentLine.length) {
            wrapped.push(currentLine)
          }
        }

        return wrapped
      }

      const maxPrintableY = labelDotsHeight - marginY

      const createLeftColumnWriter = (commands: string[]) => {
        let currentY = marginY
        return (value?: string) => {
          if (!value?.trim()) {
            return
          }

          const lines = wrapText(value, maxCharsLeftColumn)
          for (const line of lines) {
            if (currentY > maxPrintableY) {
              break
            }
            // TEXT places strings at absolute coordinates: `leftColumnX`
            // defines the starting column, `currentY` increments per line, and
            // font "1" at scale 1x2 yields a legible 203dpi body size.
            commands.push(
              `TEXT ${leftColumnX},${currentY},"1",0,1,2,"${sanitizeText(
                line
              )}"`
            )
            currentY += lineHeight
          }
        }
      }

      const createRightColumnWriter = (commands: string[]) => {
        let lineIndex = 0
        return (value?: string) => {
          if (!value?.toString().trim() || rightColumnWidth <= 0) {
            return
          }
          const lines = wrapText(value.toString(), maxCharsRightColumn)
          for (const line of lines) {
            // Queue/order numbers share the same TEXT command but use the
            // right-side origin (`rightColumnX`) so they never collide with the
            // details printed from the left column writer.
            commands.push(
              `TEXT ${rightColumnX},${marginY + lineHeight * lineIndex},"1",0,1,2,"${sanitizeText(
                line
              )}"`
            )
            lineIndex += 1
          }
        }
      }

      for (let item of items.filter((i) => i.printSticker)) {
        const variant = item.product.productVariants.find(
          (variant: any) => variant.id === item.productVariant
        )

        const variantName = generateVariantNameFromVariantValues(
          variant?.variantValues
        )

        const addOns = item.orderedProductAddOns ?? []

        // Get ingredients from product recipe if available and showMaterialsOnStickers is enabled
        const ingredients =
          posSettings?.showMaterialsOnStickers && item.product.recipe
            ? item.product.recipe.map(
                (recipe) =>
                  `${recipe.ingredient?.name || "Unknown"} x${recipe.quantity}`
              )
            : []

        // Format expiration date if available
        const expirationDate = item.product.expirationDate
          ? new Date(item.product.expirationDate).toLocaleDateString()
          : null

        const tsplCommands = [
          // Define the printable canvas in millimeters so TSPL maps dots to the
          // configured sticker dimensions instead of the printer defaults.
          `SIZE ${labelWidthInMm} mm,${labelHeightInMm} mm`,
          // Set the inter-label gap so the printer advances to the next blank
          // sticker after each job before clearing the render buffer.
          `GAP ${labelGapInMm},0`,
          // CLS clears any commands buffered from previous stickers.
          "CLS",
        ]

        const writeLeftColumn = createLeftColumnWriter(tsplCommands)
        const writeRightColumn = createRightColumnWriter(tsplCommands)

        writeRightColumn(orderNumber ? `#${orderNumber}` : undefined)
        writeRightColumn(queueNumber ? `${queueNumber}` : undefined)

        // Use location-based product name if available, fallback to global name
        const productName =
          (locationId &&
            item.product.productLocation?.find(
              (pl: any) => pl.locationId === locationId
            )?.name) ||
          item.product.name

        writeLeftColumn(productName)
        writeLeftColumn(item.notes)
        writeLeftColumn(expirationDate ? `Exp: ${expirationDate}` : undefined)
        writeLeftColumn(variantName ? `(${variantName})` : undefined)
        writeLeftColumn(customerName ? `Customer: ${customerName}` : undefined)
        addOns?.forEach((addOn) =>
          writeLeftColumn(`- ${addOn.productAddOns.name}`)
        )
        if (ingredients.length > 0) {
          writeLeftColumn("Ingredients:")
          writeLeftColumn(ingredients.join(", "))
        }

        // PRINT executes the accumulated drawing commands; issuing the order
        // quantity here guarantees identical stickers without re-laying text.
        tsplCommands.push(`PRINT ${Math.max(item.qty, 1)}`)
        const encoder = new TextEncoder()
        const payload = tsplCommands
          .filter((command) => Boolean(command?.length))
          .map((c) => c + "\r\n")
          .join("")
        const commandBuffer = encoder.encode(payload)

        await label.printer?.print(commandBuffer)
      }
    },
    openDrawer: async () => {
      const openDrawer = encodeOpenDrawer()
      const receipt = get().receipt

      receipt.printer?.print(openDrawer)
    },
    isReceiptConnected: () => get().receipt.connected || get().imin.connected,
    printKitchenReceipt: async (
      id: string,
      trx: KitchenPrinterItem,
      activeUser: string,
      business: Business,
      currency: string = "USD",
      printerGroups?: GetKitchenPrinterGroupResponse,
      optionParam?: KitchenPrinterOptions
    ) => {
      const printers = get().kitchenPrinters
      const kitchenPrinter = printers.find((kp) => kp.id === id)
      if (!kitchenPrinter) {
        throw new Error("PRINTER_NOT_FOUND")
      }
      if (!kitchenPrinter.connected) {
        throw new Error("NOT_CONNECTED")
      }

      const group = printerGroups?.find(
        (kp) => kp.id === kitchenPrinter.printerGroup
      )

      const option = {
        ...optionParam,
        variant: group?.variant,
      }

      const updatedTrx = cloneDeep(trx)
      if (group?.categories?.length) {
        updatedTrx.products = updatedTrx.products.filter((p) => {
          return group.categories?.find((c) => c.id === p.categoryId)
        })
      }

      if (!updatedTrx.products.length) {
        return
      }

      if (group?.grouping === "PER_ITEM") {
        for (const product of updatedTrx.products) {
          let receipt: Uint8Array
          const singleItem = { ...updatedTrx, products: [product] }
          if (option?.format === "FULL") {
            receipt = encodeFullKitchenPrinter(
              singleItem,
              activeUser,
              business,
              currency,
              option
            )
          } else {
            receipt = encodeKitchenPrinter(
              singleItem,
              activeUser,
              business,
              option
            )
          }
          await kitchenPrinter.printer?.print(receipt)
        }
        return
      }

      let receipt: Uint8Array
      if (option?.format === "FULL") {
        receipt = encodeFullKitchenPrinter(
          updatedTrx,
          activeUser,
          business,
          currency,
          option
        )
      } else {
        receipt = encodeKitchenPrinter(updatedTrx, activeUser, business, option)
      }

      await kitchenPrinter.printer?.print(receipt)
    },
    printBill: async (
      trx: KitchenPrinterItem,
      activeUser: string,
      business: Business,
      currency?: string,
      optionParam?: KitchenPrinterOptions
    ) => {
      const receiptPrinter = get().receipt
      if (!trx.products.length) {
        return
      }
      let receipt = encodeFullKitchenPrinter(
        trx,
        activeUser,
        business,
        currency,
        optionParam
      )
      await receiptPrinter.printer?.print(receipt)
    },
    printTableQRCode: async (details: {
      tableName: string
      url: string
      expiryDate: string
      isStatic: boolean
    }) => {
      const { receipt } = get()
      if (!receipt.connected) {
        throw new Error("NOT_CONNECTED")
      }

      const qrCode = tableQRCCodes(details)
      await receipt.printer?.print(qrCode)
    },
    clearReceipt: async () => {
      const { receipt } = get()
      if (receipt.printer?.disconnect) {
        await receipt.printer?.disconnect()
      }
      set({ receipt: DEFAULT_STATE.receipt })
      savePrinterSettings()
    },
    clearLabel: () => {
      const { label } = get()
      if (label.printer) {
        label.printer?.disconnect()
      }
      set({ label: DEFAULT_STATE.label })
      savePrinterSettings()
    },
    addKitchenPrinter: () => {
      const { kitchenPrinters } = get()
      const id = nanoid()
      set({
        kitchenPrinters: [
          ...kitchenPrinters,
          {
            printer: null,
            connected: false,
            printerGroup: "",
            deviceId: null,
            id,
            type: "BLUETOOTH",
          },
        ],
      })
    },
    clearKitchenPrinter: (id: string) => {
      const { kitchenPrinters } = get()
      const updated = cloneDeep(kitchenPrinters)
      const target = updated.find((kp) => kp.id === id)
      if (target?.printer) {
        if (target?.printer?.disconnect) {
          target.printer.disconnect()
        }
        target.connected = false
        target.deviceId = null
      }
      set({ kitchenPrinters: updated })
      savePrinterSettings()
    },
    disconnectAll: async () => {
      const { receipt, label, kitchenPrinters } = get()
      if (receipt.printer?.disconnect) {
        await receipt.printer?.disconnect()
      }
      if (label.printer) {
        await label.printer?.disconnect()
      }
      kitchenPrinters.forEach((kp) => {
        if (kp.printer?.disconnect) {
          kp.printer?.disconnect()
        }
      })

      set({
        receipt: {
          ...receipt,
          printer: null,
          connected: false,
        },
      })
      set({
        label: {
          ...label,
          printer: null,
          connected: false,
        },
      })
      set({
        kitchenPrinters: kitchenPrinters.map((kp) => ({
          ...kp,
          printer: null,
          connected: false,
        })),
      })
      // Save settings after successful connection
      savePrinterSettings()
    },
    printXReport: async (
      details: {
        storeName: string
        operatedBy: string
        address: string
        vatRegTin: string
        min: string
        serialNo: string
        reportDate: string
        reportTime: string
        startDate: string
        endDate: string

        cashier: string

        begOR: string
        endOR: string

        openingFund: string

        // ---
        // Payment Received
        paymentReceivedBreakdown: Array<{
          paymentMethodCode: string
          sum: number
        }>
        totalPayments: string

        // ---

        void: string

        // ---

        refund: string

        // ---

        withdrawal: string

        // ---
        // Transaction Summary
        cashInDrawer: string
        transactionSummaryBreakdown: Array<{
          paymentMethodCode: string
          sum: number
        }>
        lessWithdrawal: string
        paymentReceived: string

        // ---
        shortOver: string
        // ---
        country?: string
        settings: InvoiceSettingsResponseBody[number]
        latestItemsSold?: any[]
        latestItemsRefunded?: any[]
        totalTransaction?: string
        serviceCharge?: number | boolean
      },
      encoderOptions: ThermalPrinterEncoderOption = {
        language: "esc-pos",
        columns: 32,
      }
    ) => {
      const charWidth = convertPaperWidthToCharWidth(details.settings?.width)
      const encodedValues = encodeBIRXReport(details, {
        width: charWidth,
      })
      const receipt = get().receipt
      if (!receipt.connected) {
        throw new Error("NOT_CONNECTED")
      }
      await receipt.printer?.print(encodedValues.encode())
    },
    printTransferOrder: async (
      transferOrder: StockTransfer,
      items: Array<{
        itemName: string
        quantity: number
        pricePerQuantity: number
        totalPrice: number
      }>,
      encoderOptions: ThermalPrinterEncoderOption = {
        language: "esc-pos",
        columns: 32,
      }
    ) => {
      const receiptPrinter = get().receipt
      let receipt = encodeTransferOrder(transferOrder, items, encoderOptions)
      await receiptPrinter.printer?.print(receipt)
    },
    printShiftReport: async (
      shiftReport: any,
      encoderOptions: ThermalPrinterEncoderOption = {
        language: "esc-pos",
        columns: 32,
      }
    ) => {
      const receiptPrinter = get().receipt
      let receipt = encodeShiftReport(shiftReport, encoderOptions)
      await receiptPrinter.printer?.print(receipt)
    },
    printItemLabels: async (
      items: Array<{
        name: string
        price: string
        barcode: string
        quantity: number
      }>,
      printName: boolean = true,
      printPrice: boolean = true,
      encoderOptions: ThermalPrinterEncoderOption = {
        language: "esc-pos",
        columns: 32,
      }
    ) => {
      const receiptPrinter = get().receipt
      let receipt = await encodeItemLabels(
        items,
        printName,
        printPrice,
        encoderOptions
      )
      await receiptPrinter.printer?.print(receipt)
    },
    syncWithCompanionApp: async (
      kitchenPrinters: Array<{
        id: number
        kitchenPrinterGroupId: string
      }>
    ) => {
      const printer = new CompanionAppThermalPrinter("RECEIPT")
      set({
        receipt: {
          printer,
          connected: true,
          type: "BLUETOOTH",
          deviceId: nanoid(),
        },
      })

      const newKitchenPrinters = kitchenPrinters.map((kp) => {
        const printer = new CompanionAppThermalPrinter(
          "KITCHEN",
          kp.id.toString()
        )
        return {
          printer,
          connected: true,
          printerGroup: kp.kitchenPrinterGroupId,
          deviceId: kp.id.toString(),
          id: nanoid(),
          type: "BLUETOOTH" as const,
        }
      })
      set({ kitchenPrinters: newKitchenPrinters })
      // Don't save settings for companion app sync to avoid conflicts

      return {
        text: "Connected to companion app",
        value: 0,
      }
    },
    setLabelWidth: (width: number) => {
      const { label } = get()
      set({
        label: { ...label, width },
      })
      // Save settings after successful connection
      savePrinterSettings()
    },
    setLabelHeight: (height: number) => {
      const { label } = get()
      set({
        label: { ...label, height },
      })
      // Save settings after successful connection
      savePrinterSettings()
    },
    printGiftCard: async (
      business: Business,
      receiptSetting: InvoiceSettingsResponseBody[number],
      location: {
        name: string
      },
      giftCard: GiftCard
    ) => {
      const state = get()
      const encoderOptions: ThermalPrinterEncoderOption = {
        columns: 32,
        language: "esc-pos",
        feedBeforeCut: 1,
      }
      if (receiptSetting.width === 57) {
        encoderOptions.columns = 32
      } else if (receiptSetting.width === 76) {
        encoderOptions.columns = 42
      } else if (receiptSetting.width === 80) {
        encoderOptions.columns = 48
      }

      const toPrint = await encodeGiftCard(
        business,
        giftCard,
        location,
        encoderOptions
      )
      await state.receipt.printer?.print(toPrint)
    },
    printDeliveryOrder: async (
      deliveryOrder: DeliveryOrderDetails,
      receiptSetting: InvoiceSettingsResponseBody[number]
    ) => {
      const state = get()
      const encoderOptions: ThermalPrinterEncoderOption = {
        columns: 32,
        language: "esc-pos",
        feedBeforeCut: 1,
      }
      if (receiptSetting.width === 57) {
        encoderOptions.columns = 32
      } else if (receiptSetting.width === 76) {
        encoderOptions.columns = 42
      } else if (receiptSetting.width === 80) {
        encoderOptions.columns = 48
      }

      const toPrint = await encodeDeliveryOrder(deliveryOrder, encoderOptions)
      await state.receipt.printer?.print(toPrint)
    },
    /**
     * Prints a pay-in or pay-out receipt after a cash management transaction.
     *
     * This method encodes the provided pay-in/out payload into ESC/POS commands and
     * sends them to the connected thermal printer. If no printer is connected,
     * this function attempts to request and connect a device before printing.
     *
     * @param payload - Pay-in/out receipt data (transaction details and business info).
     */
    printPayInOutReceipt: async (payload: PayInOutReceiptPayload) => {
      const state = get()
      if (!state.receipt.printer || !state.receipt.connected) {
        return
        // throw new Error('No thermal printer connected')
      }
      const encoded = await encodePayInOutReceipt(payload)
      await state.receipt.printer.print(encoded)
    },
  }))
)

const verifyStatus = (status: number) => {
  if (status === -1 || status === 1) {
    throw new Error("Printer is not connected or powered on.")
  } else if (status === 3) {
    throw new Error("Printer is opened")
  } else if (status === 7) {
    throw new Error("No paper feed found")
  } else if (status === 8) {
    throw new Error("Printer is running out of paper")
  } else if (status === 99) {
    throw new Error("Printer is in an unknown state")
  } else if (status !== 0) {
    throw new Error("Unknown error")
  }

  return true
}

export const convertPaperWidthToCharWidth = (width?: number) => {
  if (width === 57) {
    return 32
  } else if (width === 76) {
    return 42
  } else if (width === 80) {
    return 48
  }

  return 32
}

// Helper functions for saving and loading settings
const savePrinterSettings = () => {
  const state = useThermalPrinter.getState()
  const settings = {
    receipt: {
      type: state.receipt.type,
      deviceId: state.receipt.deviceId,
    },
    kitchenPrinters: state.kitchenPrinters.map((kp) => ({
      id: kp.id,
      deviceId: kp.deviceId,
      printerGroup: kp.printerGroup,
      type: kp.type,
    })),
    label: {
      type: state.label.type,
      deviceId: state.label.deviceId,
      width: state.label.width,
      height: state.label.height,
    },
    imin: {
      width: state.imin.width,
    },
  }
  localStorage.setItem("thermal-printer-settings-v2", JSON.stringify(settings))
}

const loadPrinterSettings = () => {
  const saved = localStorage.getItem("thermal-printer-settings-v2")
  if (!saved) return null
  try {
    return JSON.parse(saved)
  } catch (e) {
    return null
  }
}

export const createThermalPrinter = async (
  type: "BLUETOOTH" | "USB" | "SERIAL"
) => {
  let printer: ThermalPrinter
  if (type === "BLUETOOTH") {
    printer = new BluetoothThermalPrinter()
  } else if (type === "SERIAL") {
    printer = new SerialThermalPrinter()
  } else {
    printer = new UsbThermalPrinter()
  }
  await printer.connect()
  return printer
}

export default useThermalPrinter
